import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import {
  addPortfolioVideo,
  updatePortfolioVideo,
  removePortfolioVideo,
} from "@/redux/feature/creatorApplicationV2/creatorApplicationSliceV2";
import toast from "react-hot-toast";
import type { PortfolioVideoItem } from "@/redux/feature/creatorApplicationV2/creatorApplicationTypesV2";

import LinkPlus from "@/assets/icons/authentication/LinkPlus.svg";
import VideoUploaderWithPreview from "@/components/ui/VideoUploaderWithPreview";
import type { FileDetails } from "@/types/types";
import VideoPlayerV3, { DEFAULT_BLURHASH } from "@/components/ui/VideoPlayerV3";
import {
  Pencil,
  Trash2,
  Upload,
  Link2,
  Plus,
  CheckCircle2,
  Film,
  Check,
} from "lucide-react";

// ─── tiny helper ───────────────────────────────────────────────────────────────
function cx(...classes: (string | false | undefined | null)[]) {
  return classes.filter(Boolean).join(" ");
}

// ══════════════════════════════════════════════════════════════════════════════
const PortfolioVideosSection = () => {
  const dispatch = useAppDispatch();

  const portfolioVideos = useAppSelector(
    (s) => s.signUpV2?.v2Extras?.portfolioVideos ?? [],
  );

  // ── original state (untouched) ─────────────────────────────────────────────
  const [uploadedFiles, setUploadedFiles] = useState<FileDetails[]>([]);
  const [draftLink, setDraftLink] = useState("");
  const [draftBrand, setDraftBrand] = useState("");
  const [draftPersonal, setDraftPersonal] = useState(false);
  const [draftDesc, setDraftDesc] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [playingUrl, setPlayingUrl] = useState("");

  // ── UI-only state ──────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<"upload" | "link">("upload");
  const [linkVerified, setLinkVerified] = useState(false);

  const uploaded = uploadedFiles[0] || null;
  const uploadReady = !!uploaded?.live_url;
  const hasUpload = uploadedFiles.length > 0;
  const hasLink = draftLink.trim().length > 0;
  const effectiveMode = hasUpload ? "upload" : hasLink ? "link" : "upload";

  const canSubmit = useMemo(() => {
    if (!draftDesc.trim()) return false;
    if (hasUpload) return uploadReady;
    if (hasLink) {
      try {
        new URL(draftLink.trim());
        return true;
      } catch {
        return false;
      }
    }
    return false;
  }, [uploadReady, hasUpload, hasLink, draftLink, draftDesc]);

  const resetDraft = () => {
    setUploadedFiles([]);
    setDraftLink("");
    setDraftBrand("");
    setDraftPersonal(false);
    setDraftDesc("");
    setEditingId(null);
    setBusy(false);
    setLinkVerified(false);
  };

  const startEdit = (item: PortfolioVideoItem) => {
    setEditingId(item.id);
    setDraftBrand(item.brandName ?? "");
    setDraftPersonal(!!item.isPersonalProject);
    setDraftDesc(item.description ?? "");
    if (item.sourceType === "link") {
      setDraftLink(item.linkUrl ?? "");
      setUploadedFiles([]);
      setActiveTab("link");
    } else {
      setDraftLink("");
      setUploadedFiles(item.file ? [item.file as unknown as FileDetails] : []);
      setActiveTab("upload");
    }
  };

  const handleDelete = (id: string) => {
    dispatch(removePortfolioVideo({ id }));
    if (editingId === id) resetDraft();
  };

  const handleAddOrSave = async () => {
    if (busy) return;
    if (!draftDesc.trim()) return toast.error("Please add a description.");
    const mode = effectiveMode;
    if (mode === "upload" && !uploadReady)
      return toast.error("Please wait until the upload finishes.");
    if (mode === "link") {
      if (!draftLink.trim()) return toast.error("Please add a video link.");
      try {
        new URL(draftLink.trim());
      } catch {
        return toast.error("Please provide a valid link URL.");
      }
    }
    if (!hasUpload && !hasLink)
      return toast.error("Please upload a video or paste a link.");

    setBusy(true);
    try {
      const basePatch: Partial<PortfolioVideoItem> = {
        brandName: draftBrand.trim(),
        isPersonalProject: draftPersonal,
        description: draftDesc.trim(),
      };

      if (editingId) {
        dispatch(
          updatePortfolioVideo({
            id: editingId,
            patch:
              mode === "upload"
                ? {
                    ...basePatch,
                    sourceType: "upload",
                    linkUrl: undefined,
                    file: uploaded as any,
                    uploading: false,
                    uploadError: null,
                  }
                : {
                    ...basePatch,
                    sourceType: "link",
                    linkUrl: draftLink.trim(),
                    file: undefined,
                    uploading: false,
                    uploadError: null,
                  },
          }),
        );
        toast.success("Updated!");
        resetDraft();
        return;
      }

      const id = crypto.randomUUID();
      dispatch(
        addPortfolioVideo(
          mode === "upload"
            ? ({
                id,
                ...basePatch,
                sourceType: "upload",
                file: uploaded as any,
                uploading: false,
                uploadError: null,
                createdAt: new Date().toISOString(),
              } as PortfolioVideoItem)
            : ({
                id,
                ...basePatch,
                sourceType: "link",
                linkUrl: draftLink.trim(),
                uploading: false,
                uploadError: null,
                createdAt: new Date().toISOString(),
              } as PortfolioVideoItem),
        ),
      );
      toast.success("Added!");
      resetDraft();
    } catch (e) {
      console.error(e);
      toast.error("Failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // ── derived ────────────────────────────────────────────────────────────────
  const descLen = draftDesc.trim().length;
  const hasVideos = portfolioVideos.length > 0;

  return (
    <div className="w-full space-y-5">
      {/* ── Section header ─────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <h6 className="text-[#121217] text-sm font-semibold tracking-tight">
            Portfolio Videos
          </h6>
          <p className="text-[#8A94A6] text-[10px] mt-0.5 leading-relaxed">
            UGC, brand ads, reels — add what shows your work best.
          </p>
        </div>
        {hasVideos && (
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#FF6600] bg-[#FFF4EC] border border-[#FFD5B0] px-2 py-0.5 rounded-full">
            <Film size={10} />
            {portfolioVideos.length} added
          </span>
        )}
      </div>

      {/* ── Form card ──────────────────────────────────────────────────────── */}
      <div
        className="rounded-2xl border border-[#E8EBF0] bg-white overflow-hidden"
        style={{
          boxShadow: "0 1px 4px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04)",
        }}
      >
        {/* Tab bar */}
        <div className="grid grid-cols-2 bg-[#F8F9FB] border-b border-[#ECEEF2]">
          {(["upload", "link"] as const).map((tab) => {
            const active = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={cx(
                  "relative flex items-center justify-center gap-2 py-3 text-[11px] font-semibold transition-all duration-200",
                  active
                    ? "text-[#FF6600] bg-white shadow-[inset_0_-2px_0_#FF6600]"
                    : "text-[#A0ABBB] hover:text-[#667085] hover:bg-[#F2F4F7]",
                )}
              >
                {tab === "upload" ? (
                  <Upload size={12} strokeWidth={2.5} />
                ) : (
                  <Link2 size={12} strokeWidth={2.5} />
                )}
                {tab === "upload" ? "Upload File" : "Paste Link"}
              </button>
            );
          })}
        </div>

        {/* Tab content */}
        <div className="p-4">
          {/* ── Upload tab ─────────────────────────────────────────────────── */}
          {activeTab === "upload" && (
            <div className="flex gap-4 items-start">
              {/* Uploader zone */}
              <div className="shrink-0 flex flex-col items-center gap-1.5">
                <div
                  className={cx(
                    "rounded-xl overflow-hidden transition-all duration-200",
                    hasUpload && uploadReady
                      ? "ring-2 ring-[#FF6600] ring-offset-1"
                      : hasUpload && !uploadReady
                        ? "ring-2 ring-[#FFA500] ring-offset-1"
                        : "ring-1 ring-[#E8EBF0]",
                  )}
                >
                  <VideoUploaderWithPreview
                    maxFiles={1}
                    useExternalState
                    externalFiles={uploadedFiles}
                    setExternalFiles={setUploadedFiles}
                    showSelectedFiles
                    showUploadAnimationOnly={false}
                    useLiveUrl={false}
                    height="116px"
                    width="116px"
                    cardDesignSize="h-[116px] w-[116px]"
                    accept={{
                      "video/mp4": [],
                      "video/webm": [],
                      "video/ogg": [],
                      "video/quicktime": [],
                    }}
                    onFilesUploaded={() => {}}
                  />
                </div>
                {hasUpload && !uploadReady && (
                  <span className="text-[9px] text-[#FFA500] font-medium animate-pulse">
                    Uploading…
                  </span>
                )}
                {hasUpload && uploadReady && (
                  <span className="flex items-center gap-1 text-[9px] text-[#16A34A] font-semibold">
                    <CheckCircle2 size={10} /> Ready
                  </span>
                )}
              </div>

              {/* Meta */}
              <div className="flex-1 space-y-2.5 min-w-0">
                <MetaFields
                  draftBrand={draftBrand}
                  setDraftBrand={setDraftBrand}
                  draftPersonal={draftPersonal}
                  setDraftPersonal={setDraftPersonal}
                  draftDesc={draftDesc}
                  setDraftDesc={setDraftDesc}
                />
                <FormActions
                  busy={busy}
                  canSubmit={canSubmit}
                  editingId={editingId}
                  handleAddOrSave={handleAddOrSave}
                  resetDraft={resetDraft}
                />
              </div>
            </div>
          )}

          {/* ── Link tab ───────────────────────────────────────────────────── */}
          {activeTab === "link" && (
            <div className="space-y-3">
              {/* URL input */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-semibold text-[#667085] tracking-wide uppercase">
                  Video URL
                </label>

                <div
                  className={cx(
                    "flex items-center gap-2 rounded-xl border bg-[#F8F9FB] px-3 py-2.5 transition-all duration-200",
                    linkVerified
                      ? "border-[#16A34A] bg-[#F0FDF4]"
                      : draftLink && !linkVerified
                        ? "border-[#FF6600] bg-white shadow-[0_0_0_3px_rgba(255,102,0,0.08)]"
                        : "border-[#E8EBF0] focus-within:border-[#FF6600] focus-within:bg-white focus-within:shadow-[0_0_0_3px_rgba(255,102,0,0.08)]",
                  )}
                >
                  {linkVerified ? (
                    <CheckCircle2
                      size={14}
                      className="text-[#16A34A] shrink-0"
                    />
                  ) : (
                    <Link2 size={14} className="text-[#A0ABBB] shrink-0" />
                  )}
                  <input
                    type="text"
                    placeholder="https://youtube.com/watch?v=..."
                    value={draftLink}
                    onChange={(e) => {
                      setDraftLink(e.target.value);
                      setLinkVerified(false);
                      if (e.target.value && uploadedFiles.length > 0)
                        setUploadedFiles([]);
                    }}
                    className="flex-1 bg-transparent text-[11px] text-[#121217] placeholder:text-[#C1C9D2] outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!draftLink.trim()) return;
                      try {
                        new URL(draftLink.trim());
                        setLinkVerified(true);
                        toast.success("Link looks good!");
                      } catch {
                        setLinkVerified(false);
                        toast.error(
                          "Invalid URL — check the link and try again.",
                        );
                      }
                    }}
                    className={cx(
                      "shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all duration-150",
                      linkVerified
                        ? "bg-[#DCFCE7] text-[#16A34A] border border-[#BBF7D0]"
                        : "bg-[#FFF4EC] text-[#FF6600] border border-[#FFD5B0] hover:bg-[#FFEDD5]",
                    )}
                  >
                    {linkVerified ? (
                      <>
                        <CheckCircle2 size={11} /> Verified
                      </>
                    ) : (
                      <>
                        <LinkPlus /> Verify
                      </>
                    )}
                  </button>
                </div>

                {/* Supported platforms pill row */}
                <div className="flex flex-wrap gap-1 pt-0.5">
                  {["YouTube", "Instagram", "TikTok", "Drive", "Dropbox"].map(
                    (p) => (
                      <span
                        key={p}
                        className="text-[9px] font-medium text-[#A0ABBB] bg-[#F2F4F7] px-2 py-0.5 rounded-full"
                      >
                        {p}
                      </span>
                    ),
                  )}
                </div>
              </div>

              <MetaFields
                draftBrand={draftBrand}
                setDraftBrand={setDraftBrand}
                draftPersonal={draftPersonal}
                setDraftPersonal={setDraftPersonal}
                draftDesc={draftDesc}
                setDraftDesc={setDraftDesc}
              />
              <FormActions
                busy={busy}
                canSubmit={canSubmit}
                editingId={editingId}
                handleAddOrSave={handleAddOrSave}
                resetDraft={resetDraft}
              />
            </div>
          )}
        </div>
      </div>

      {/* ── Video list ─────────────────────────────────────────────────────── */}
      {hasVideos && (
        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold text-[#374151]">
              Your videos
            </p>
            <button
              type="button"
              onClick={resetDraft}
              className="flex items-center gap-1 text-[11px] font-semibold text-[#FF6600] hover:opacity-75 transition-opacity"
            >
              <Plus size={12} strokeWidth={2.5} />
              Add more
            </button>
          </div>

          <div className="space-y-2">
            {portfolioVideos.map((v, idx) => {
              const url =
                v.sourceType === "upload"
                  ? (v.file?.live_url as string | undefined)
                  : (v.linkUrl as string | undefined);
              const thumb =
                v.sourceType === "upload" ? (v.file?.thumbnail as string) : "";
              const hash =
                v.sourceType === "upload"
                  ? (v.file?.hash as string) || DEFAULT_BLURHASH
                  : DEFAULT_BLURHASH;
              const isEditing = editingId === v.id;

              return (
                <div
                  key={v.id}
                  className={cx(
                    "group bg-white rounded-2xl border overflow-hidden transition-all duration-200",
                    isEditing
                      ? "border-[#FF6600] shadow-[0_0_0_3px_rgba(255,102,0,0.1)]"
                      : "border-[#E8EBF0] hover:border-[#D0D5DD] hover:shadow-[0_2px_12px_rgba(0,0,0,0.06)]",
                  )}
                  style={{
                    boxShadow: isEditing
                      ? undefined
                      : "0 1px 3px rgba(0,0,0,0.04)",
                  }}
                >
                  <div className="flex gap-0 items-stretch">
                    {/* Thumbnail / player column */}
                    <div className="w-[80px] shrink-0 bg-[#F2F4F7] relative overflow-hidden">
                      {v.sourceType === "upload" && url ? (
                        <VideoPlayerV3
                          url={url}
                          thumbnailUrl={thumb}
                          playing={playingUrl === url}
                          muted={false}
                          blurhash={hash}
                          onPlay={() =>
                            setPlayingUrl((cur) => (cur === url ? "" : url))
                          }
                          border="rounded-none"
                        />
                      ) : v.sourceType === "link" ? (
                        <div className="w-full h-full min-h-[110px] flex flex-col items-center justify-center gap-2 p-2">
                          <div className="w-9 h-9 rounded-full bg-white border border-[#FFD5B0] shadow-sm flex items-center justify-center">
                            <Link2 size={15} className="text-[#FF6600]" />
                          </div>
                          <p className="text-[7.5px] text-[#A0ABBB] text-center leading-tight break-all line-clamp-2 px-1">
                            {v.linkUrl?.replace(/^https?:\/\//, "")}
                          </p>
                        </div>
                      ) : (
                        <div className="w-full min-h-[110px] flex items-center justify-center">
                          <Film size={20} className="text-[#D0D5DD]" />
                        </div>
                      )}

                      {/* Index badge */}
                      <span className="absolute top-1.5 left-1.5 w-4 h-4 rounded-full bg-black/40 text-white text-[8px] font-bold flex items-center justify-center backdrop-blur-sm">
                        {idx + 1}
                      </span>
                    </div>

                    {/* Content column */}
                    <div className="flex-1 px-3 py-2.5 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          {/* Title */}
                          <p className="text-[12px] font-semibold text-[#121217] leading-tight truncate">
                            {v.brandName ||
                              (v.isPersonalProject
                                ? "Personal Project"
                                : "Untitled")}
                          </p>

                          {/* Type badge */}
                          <span
                            className={cx(
                              "inline-flex items-center gap-1 mt-1 text-[9px] font-semibold px-1.5 py-0.5 rounded-full",
                              v.sourceType === "upload"
                                ? "bg-[#F0FDF4] text-[#15803D] border border-[#BBF7D0]"
                                : "bg-[#FFF4EC] text-[#FF6600] border border-[#FFD5B0]",
                            )}
                          >
                            {v.sourceType === "upload" ? (
                              <>
                                <Upload size={8} strokeWidth={2.5} /> Uploaded
                              </>
                            ) : (
                              <>
                                <Link2 size={8} strokeWidth={2.5} /> Link
                              </>
                            )}
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => startEdit(v)}
                            className={cx(
                              "p-1.5 rounded-lg border transition-colors",
                              isEditing
                                ? "border-[#FF6600] bg-[#FFF4EC] text-[#FF6600]"
                                : "border-[#E8EBF0] bg-white text-[#667085] hover:bg-[#F8F9FB]",
                            )}
                            aria-label="Edit"
                          >
                            <Pencil size={12} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(v.id)}
                            className="p-1.5 rounded-lg border border-[#FDE2D1] bg-[#FFF7ED] text-[#F97316] hover:bg-[#FFEDD5] transition-colors"
                            aria-label="Delete"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>

                      {/* Description */}
                      {v.description && (
                        <p className="text-[10px] text-[#6B7280] mt-2 line-clamp-2 leading-relaxed">
                          {v.description}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Editing indicator strip */}
                  {isEditing && (
                    <div className="px-3 py-1.5 bg-[#FFF4EC] border-t border-[#FFD5B0] flex items-center gap-1.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#FF6600] animate-pulse" />
                      <p className="text-[9px] font-semibold text-[#FF6600]">
                        Editing this entry — make changes above
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Empty state ────────────────────────────────────────────────────── */}
      {!hasVideos && (
        <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-[#F8F9FB] border border-dashed border-[#D0D5DD]">
          <div className="w-7 h-7 rounded-lg bg-white border border-[#E8EBF0] flex items-center justify-center shrink-0 shadow-sm">
            <Film size={14} className="text-[#C1C9D2]" />
          </div>
          <p className="text-[10px] text-[#A0ABBB] leading-relaxed">
            No videos added yet. Upload a file or paste a link above to get
            started.
          </p>
        </div>
      )}
    </div>
  );
};

export default PortfolioVideosSection;

// ══════════════════════════════════════════════════════════════════════════════
// Shared sub-components
// ══════════════════════════════════════════════════════════════════════════════

function MetaFields({
  draftBrand,
  setDraftBrand,
  draftPersonal,
  setDraftPersonal,
  draftDesc,
  setDraftDesc,
}: {
  draftBrand: string;
  setDraftBrand: (v: string) => void;
  draftPersonal: boolean;
  setDraftPersonal: Dispatch<SetStateAction<boolean>>;

  draftDesc: string;
  setDraftDesc: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      {/* Brand + Personal row */}
      <div className="flex items-center gap-2 rounded-xl border border-[#E8EBF0] bg-[#F8F9FB] pr-3 focus-within:border-[#FF6600] focus-within:bg-white focus-within:shadow-[0_0_0_3px_rgba(255,102,0,0.08)] transition-all duration-200">
        <input
          type="text"
          placeholder="Brand name (optional)"
          value={draftBrand}
          onChange={(e) => setDraftBrand(e.target.value)}
          className="flex-1 h-9 px-3 text-[11px] text-[#121217] placeholder:text-[#C1C9D2] outline-none bg-transparent"
        />
        <div className="flex items-center gap-1.5 text-[10px] text-[#4B5563] font-semibold whitespace-nowrap shrink-0 select-none">
          <button
            type="button"
            onClick={() => setDraftPersonal((prev) => !prev)}
            className={`w-4 h-4 rounded flex items-center justify-center transition-colors shrink-0 ${
              draftPersonal
                ? "bg-[#FF6600]"
                : "bg-white border border-[#D0D5DD]"
            }`}
          >
            {draftPersonal && (
              <Check className="w-3 h-3 text-white stroke-[3]" />
            )}
          </button>
          Personal
        </div>
      </div>

      {/* Description */}
      <div className="relative">
        <textarea
          placeholder="Describe the video — brand goal, your role, deliverable type…"
          rows={3}
          value={draftDesc}
          onChange={(e) => setDraftDesc(e.target.value)}
          className="w-full px-3 py-2.5 rounded-xl border border-[#E8EBF0] bg-[#F8F9FB]
            focus:border-[#FF6600] focus:bg-white focus:shadow-[0_0_0_3px_rgba(255,102,0,0.08)]
            text-[11px] text-[#121217] placeholder:text-[#C1C9D2]
            outline-none resize-none transition-all duration-200 leading-relaxed"
        />
        {draftDesc.length > 0 && (
          <span className="absolute bottom-2 right-2.5 text-[8px] text-[#C1C9D2] font-medium pointer-events-none">
            {draftDesc.trim().length} chars
          </span>
        )}
      </div>
    </div>
  );
}

function FormActions({
  busy,
  canSubmit,
  editingId,
  handleAddOrSave,
  resetDraft,
}: {
  busy: boolean;
  canSubmit: boolean;
  editingId: string | null;
  handleAddOrSave: () => void;
  resetDraft: () => void;
}) {
  return (
    <div className="flex items-center gap-2 pt-0.5">
      <button
        type="button"
        onClick={handleAddOrSave}
        disabled={busy || !canSubmit}
        className="flex items-center gap-1.5 py-2 px-4 rounded-full text-[11px] font-semibold text-white
          bg-[#FF6600] hover:bg-[#E55A00] disabled:opacity-40 disabled:cursor-not-allowed
          shadow-[0_1px_4px_rgba(255,102,0,0.35)] hover:shadow-[0_2px_8px_rgba(255,102,0,0.4)]
          active:scale-[0.98] transition-all duration-150"
      >
        {editingId ? "Save Changes" : busy ? "Saving…" : "Add to Profile"}
      </button>

      {editingId && (
        <button
          type="button"
          onClick={resetDraft}
          className="py-2 px-4 rounded-full border border-[#E8EBF0] bg-white text-[#4B5563] text-[11px] font-semibold
            hover:bg-[#F8F9FB] hover:border-[#D0D5DD] active:scale-[0.98] transition-all duration-150"
        >
          Cancel
        </button>
      )}
    </div>
  );
}
